import { AuthProvidersEnum } from 'src/shared';
import { FiltersDataForAnalytics } from 'src/shared/types/filters';

export enum FunnelTypeEnum {
  DEFAULT = 'default',
  WEB_TO_WEB = 'web_to_web',
  WEB_TO_APP = 'web_to_app',
}

type FunnelPropsType = {
  funnel_id: string;
  funnel_name: string;
  funnel_type: FunnelTypeEnum;
  onboarding_experiment_name?: string;
};
type FunnelScreenPropsType = {
  screen_id: string;
  screen_name: string;
  screen_order: number;
};
type RatingScreenPropsType = {
  specialist_id: string;
  specialist_name: string;
  consultation_id: string;
  subscription_id?: string | null;
};

export interface AnalyticsEventConfig<P> {
  eventName: string;
  props?: P;
  // List of service IDs that should receive this event.
  services?: string[];
  ignoreGlobalProps?: boolean;
  ignoreGloablEventProps?: string[];
}

export interface AnalyticsService {
  id: string; // e.g., 'mixpanel', 'ga4'
  isDebugOnly: boolean;
  initialize: () => void;
  sendEvent: (eventName: string, properties?: Record<string, any>) => void;
  identify?: (userId: string, properties?: Record<string, any>) => void;
  reset?: () => void;
  incrementUserProperty?: (property: string, value: number) => void;
  setSuperProperties?: (properties: Record<string, any>) => void;
  setSuperPropertiesOnce?: (properties: Record<string, any>) => void;
  setUserProperties?: (properties: Record<string, any>) => void;
  setUserPropertiesOnce?: (properties: Record<string, any>) => void;
}

interface AdNetworkAdvancedMatchingProps {
  email?: string;
  firstName?: string;
  lastName?: string;
}
export interface AdNetworksEventsMapping {
  InitiateCheckout: AnalyticsEventConfig<
    {
      contents: [{ content_id: string; content_type: string }];
      value: number;
      currency: string;
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
  Lead: AnalyticsEventConfig<
    {
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
  CompleteRegistration: AnalyticsEventConfig<
    {
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
  Purchase: AnalyticsEventConfig<
    {
      contents: [{ content_id: string; content_type: string }];
      value: number;
      currency: string;
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
  Subscribe: AnalyticsEventConfig<
    {
      contents: [{ content_id: string; content_type: string }];
      value: number;
      currency: string;
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
}

export interface TikTokPixelEventsMapping extends AdNetworksEventsMapping {}

export interface FacebookPixelEventsMapping extends AdNetworksEventsMapping {
  ViewContent: AnalyticsEventConfig<
    {
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
  AppOpened: AnalyticsEventConfig<
    {
      eventInstanceId: string;
      userId: string;
    } & AdNetworkAdvancedMatchingProps
  >;
}

export interface FirebaseEventsMapping {
  refund: AnalyticsEventConfig<{
    currency: string;
    value: number;
  }>;
  purchase: AnalyticsEventConfig<{
    currency: string;
    value: number;
    specialist_name?: string;
    specialist_id?: string;
    platform: string;
    product_name?: string;
    product_id?: string;
  }>;
  purchase_subscription: AnalyticsEventConfig<{
    currency: string;
    value: number;
    specialist_name: string;
    specialist_id: string;
    platform: string;
  }>;
  client_sign_up: AnalyticsEventConfig<{
    clientId: string;
  }>;
}

export interface AnalyticsEventsMapping
  extends TikTokPixelEventsMapping,
    FirebaseEventsMapping,
    FacebookPixelEventsMapping {
  FUNNEL_STARTED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType & { locale: string }>;
  FUNNEL_START_CLICKED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType & { locale: string }>;
  FUNNEL_SKIP_CLICKED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType & { locale: string }>;
  FUNNEL_SCREEN_VIEWED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType>;
  FUNNEL_ANSWERS: AnalyticsEventConfig<
    FunnelPropsType & FunnelScreenPropsType & { answers: { id: string; question?: string; answer: string }[] }
  >;
  FUNNEL_COMPLETED: AnalyticsEventConfig<FunnelPropsType & { $user_id: string }>;
  FUNNEL_CONTINUE_CLICKED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType>;
  FUNNEL_REGISTRATION_EMAIL_SCREEN_VIEWED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType>;
  FUNNEL_REGISTRATION_CONFIRMATION_SCREEN_VIEWED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType>;
  FUNNEL_REGISTRATION_EMAIL_CONTINUE_CLICKED: AnalyticsEventConfig<
    FunnelPropsType & FunnelScreenPropsType & { user_email: string; verification_message: string }
  >;
  FUNNEL_REGISTRATION_CONFIRMATION_CONTINUE_CLICKED: AnalyticsEventConfig<
    FunnelPropsType & FunnelScreenPropsType & { user_email: string; verification_message: string }
  >;
  FUNNEL_PAYWALL_SCREEN_VIEWED: AnalyticsEventConfig<FunnelPropsType & FunnelScreenPropsType & { user_email: string }>;
  FUNNEL_PAYWALL_SCROLL_50_PERCENT: AnalyticsEventConfig<
    FunnelPropsType & FunnelScreenPropsType & { user_email: string }
  >;
  FUNNEL_PAYWALL_SCROLL_100_PERCENT: AnalyticsEventConfig<
    FunnelPropsType & FunnelScreenPropsType & { user_email: string }
  >;
  FUNNEL_PAYWALL_PURCHASE_BUTTON_CLICKED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        purchase_value: number;
        purchase_value_after_discount: number;
        purchase_currency: string;
        purchase_plan: string;
      }
  >;
  FUNNEL_PAYWALL_GET_MY_SESSION_CLICKED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
      }
  >;
  FUNNEL_PAYWALL_PLAN_INFO_VIEWED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        purchase_value: number;
        purchase_value_after_discount: number;
        purchase_currency: string;
        purchase_plan: string;
      }
  >;
  FUNNEL_CHECKOUT_SCREEN_VIEWED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        purchase_value: number;
        purchase_value_after_discount: number;
        purchase_currency: string;
        purchase_plan: string;
      }
  >;
  FUNNEL_CHECKOUT_PAY_NOW_BUTTON_CLICKED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        purchase_value: number;
        purchase_value_after_discount: number;
        purchase_currency: string;
        purchase_plan: string;
        payment_type: string;
      }
  >;
  FUNNEL_CHECKOUT_PAYMENT_RETRY_AFTER_PASSWORD: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
      }
  >;
  FUNNEL_PAYWALL_DISCOUNT_OFFERED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        promocode_value: number;
      }
  >;
  FUNNEL_PAYWALL_GET_DISCOUNT_BUTTON_CLICKED: AnalyticsEventConfig<
    FunnelPropsType &
      FunnelScreenPropsType & {
        user_email: string;
        promocode_value: number;
      }
  >;
  USER_REGISTRATION: AnalyticsEventConfig<{
    $user_id: string;
    registration_method: AuthProvidersEnum;
    $first_name: string;
    $last_name: string;
    $email: string;
  }>;
  SPECIALIST_LIST_VIEWED: AnalyticsEventConfig<
    FiltersDataForAnalytics & {
      specialist_list_algorithm_version: string;
      specialists_count: number;
      specialists_total: number;
    }
  >;
  SPECIALIST_PROFILE_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  TRIAL_SCHEDULE_SCREEN_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  TRIAL_SCHEDULE_DATE_SELECTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    date_chosen: string;
    specialist_order_position: number;
  }>;
  TRIAL_CHECKOUT_SCREEN_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    date_chosen: string;
    specialist_order_position: number;
  }>;
  TRIAL_CHECKOUT_PAY_BUTTON_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    payment_type: string;
  }>;
  SUBSCRIPTION_PAGE_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SUBSCRIPTION_CHECKOUT_SCREEN_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    subscription_length: number;
    specialist_order_position: number;
  }>;
  SUBSCRIPTION_SCHEDULE_SCREEN_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    subscription_id: string;
    subscription_length: number;
    subscription_balance: number;
    specialist_order_position: number;
    available_times_slots: {
      date: string;
      available_times: string[];
    };
  }>;
  EXPERIMENT_STARTED: AnalyticsEventConfig<{
    'Experiment name': string;
    'Variant name': string;
  }>;
  SPECIALIST_LIST_FILTER_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  SPECIALIST_LIST_FILTERS_SUBMITTED: AnalyticsEventConfig<FiltersDataForAnalytics>;
  SPECIALIST_LIST_SPECIALIST_PROFILE_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    specialist_list_algorithm_version: string;
  }>;
  SPECIALIST_PROFILE_SCROLL_50_PERCENT: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_SCROLL_100_PERCENT: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  TRIAL_SCHEDULE_SCREEN_SLOTS_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    available_times_slots: {
      date: string;
      available_times: string[];
    };
  }>;
  LOGIN_GET_STARTED_VIEWED: AnalyticsEventConfig<{}>;
  LOGIN_GOOGLE_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  LOGIN_APPLE_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  LOGIN_EMAIL_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  SIGNUP_ACCOUNT_ALREADY_EXISTS_VIEWED: AnalyticsEventConfig<{}>;
  SIGNUP_USER_INFO_VIEWED: AnalyticsEventConfig<{ screen_type: string }>;
  SIGNUP_USER_INFO_SUBMITTED: AnalyticsEventConfig<{
    screen_type: string;
    value?: string;
    is_login?: boolean;
    need_to_update?: boolean;
  }>;
  NOTIFICATION_PERMISSION_VIEWED: AnalyticsEventConfig<{}>;
  NOTIFICATION_PERMISSION_CLICKED: AnalyticsEventConfig<{ permission_type: string }>;
  SCHEDULE_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  SESSION_DETAILS_SCREEN_VIEWED: AnalyticsEventConfig<{ specialist_id: string; specialist_name: string }>;
  SESSION_START_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  SESSION_STARTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    consultation_id: string;
    is_trial: boolean;
    scheduled_start_time: string;
    actual_start_time: string;
    is_new_video: boolean;
    is_hybrid_platform: boolean;
  }>;
  THERAPY_SESSION_STARTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    therapy_session: string;
    scheduled_start_time: string;
    actual_start_time: string;
    platform: string;
  }>;
  THERAPY_SESSION_COMPLETED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    therapy_session: string;
    actual_end_time: string;
    session_duration: number;
  }>;
  FAVOURITES_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  CHAT_LIST_VIEWED: AnalyticsEventConfig<{ is_new: boolean }>;
  PROFILE_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  PROFILE_USER_DATA_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  PROFILE_DELETE_ACCOUNT_BUTTON_CLICKED: AnalyticsEventConfig<{}>;
  PROFILE_DELETE_ACCOUNT_BUTTON_SUBMITTED: AnalyticsEventConfig<{}>;
  SUBSCRIPTION_PAUSE_SCREEN_VIEWED: AnalyticsEventConfig<{ subscription_id: string; specialist_id: string }>;
  SUBSCRIPTION_PAUSE_CONFIRMATION_MODAL_CLICKED: AnalyticsEventConfig<{
    subscription_id: string;
    specialist_id: string;
  }>;
  SUBSCRIPTION_PAUSE_COMPLETED: AnalyticsEventConfig<{ subscription_id: string; specialist_id: string }>;
  SUBSCRIPTION_CANCEL_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  SUBSCRIPTION_CANCEL_REASON_MODAL_CLICKED: AnalyticsEventConfig<{
    subscription_id: string;
    cancel_reason: string;
    cancel_reason_code: number;
    button_back_clicked: boolean;
  }>;
  SUBSCRIPTION_CANCEL_COMPLETED: AnalyticsEventConfig<{ subscription_id: string; specialist_id: string }>;
  SUBSCRIPTION_RESTART_SCREEN_VIEWED: AnalyticsEventConfig<{ subscription_id: string; specialist_id: string }>;
  SUBSCRIPTION_RESTART_COMPLETED: AnalyticsEventConfig<{ subscription_id: string; specialist_id: string }>;
  SCHEDULE_SCREEN_RATE_SESSION_CLICKED: AnalyticsEventConfig<RatingScreenPropsType>;
  SUBSCRIPTION_CHECKOUT_PAY_BUTTON_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    payment_type: string;
  }>;
  RATING_SCREEN_VIEWED: AnalyticsEventConfig<
    RatingScreenPropsType & {
      rating_screen_place: string;
    }
  >;
  RATING_BUTTON_CLICKED: AnalyticsEventConfig<RatingScreenPropsType & { rating_value: number }>;
  RATING_REASON_SELECTED: AnalyticsEventConfig<
    RatingScreenPropsType & {
      rating_value: number;
      rating_reasons: string[];
      user_comment?: string;
    }
  >;
  RATING_BUTTON_CLOSED_CLICKED: AnalyticsEventConfig<RatingScreenPropsType>;
  RATING_DISCOUNT_OFFERED: AnalyticsEventConfig<{
    consultation_id: string;
    promocode_value: number | string;
    promocode_id: string;
  }>;
  RATING_NEXT_SESSION_SCREEN_VIEWED: AnalyticsEventConfig<RatingScreenPropsType>;
  RATING_NEXT_SESSION_CONTINUE_CLICKED: AnalyticsEventConfig<RatingScreenPropsType>;
  RATING_NEXT_SESSION_CLOSE_CLICKED: AnalyticsEventConfig<RatingScreenPropsType>;
  RATING_PUBLIC_SCREEN_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
  }>;
  RATING_PUBLIC_RATE_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    rating_value: number;
  }>;
  RATING_PUBLIC_REVIEW_SUBMITTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
  }>;
  RATING_PUBLIC_REVIEW_UPDATED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
  }>;
  NPS_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  NPS_RATING_STARS_SELECTED: AnalyticsEventConfig<{
    rating_value: number;
  }>;
  NPS_FEEDBACK_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  NPS_FEEDBACK_COMPLETED: AnalyticsEventConfig<{
    rating_value: number;
    text_feedback_provided: boolean;
  }>;
  NPS_INTERVIEW_SCREEN_VIEWED: AnalyticsEventConfig<{}>;
  NPS_INTERVIEW_SCREEN_SUBMITTED: AnalyticsEventConfig<{}>;

  SPECIALIST_PROFILE_SUPER_SPECIALIST_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_NEW_SPECIALIST_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_VIDEO_COVER_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_VIDEO_BUTTON_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_TAB_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    tab_name: string;
  }>;
  SPECIALIST_PROFILE_MORE_BUTTON_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    block_name: string;
  }>;
  SPECIALIST_PROFILE_REVIEWS_MODAL_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  SPECIALIST_PROFILE_SLOTS_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    available_times_slots: {
      date: string;
      available_times: string[];
    };
  }>;
  SPECIALIST_PROFILE_SLOT_SELECTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    date_chosen: string;
  }>;
  SPECIALIST_LIST_SLOT_SELECTED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    date_chosen: string;
  }>;
  SPECIALIST_LIST_SHOW_ALL_SLOTS_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
  }>;
  BOOKING_CONFIRMATION_MODAL_VIEWED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    date_chosen: string;
  }>;
  BOOKING_CONFIRMATION_MODAL_CLICKED: AnalyticsEventConfig<{
    specialist_id: string;
    specialist_name: string;
    specialist_order_position: number;
    date_chosen: string;
  }>;
  LOGIN_SELECT_LANGUAGE_VIEWED: AnalyticsEventConfig<{}>;
  LOGIN_SELECT_LANGUAGE_SELECTED: AnalyticsEventConfig<{
    language: string;
  }>;
  LOGIN_LANGUAGE_UNSUPPORTED_VIEWED: AnalyticsEventConfig<{}>;
  LOGIN_LANGUAGE_UNSUPPORTED_EMAIL_PROVIDED: AnalyticsEventConfig<{
    email: string;
  }>;
}

export interface AnalyticsManager {
  identify: (userId: string, properties?: Record<string, any>, targetServices?: string[]) => void;
  reset: () => void;
  trackPredefinedEvent: <K extends keyof AnalyticsEventsMapping>(
    eventKey: K,
    overrideProps?: Partial<AnalyticsEventsMapping[K]['props']>,
    targetServices?: string[]
  ) => void;
  incrementUserProperty: (property: string, value: number) => void;
  setSuperProperties: (properties: Record<string, any>, targetServices?: string[]) => void;
  setSuperPropertiesOnce: (properties: Record<string, any>, targetServices?: string[]) => void;
  setUserProperties: (properties: Record<string, any>, targetServices?: string[]) => void;
  setUserPropertiesOnce: (properties: Record<string, any>, targetServices?: string[]) => void;
}

export interface AnalyticsProviderProps {
  children: React.ReactNode;
  services: AnalyticsService[];
}
